import React, { useEffect, useState } from 'react';
import axios from 'axios';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import useUploadListFile from 'Hooks/query-hooks/list/useUploadListFile';
import useDeleteUploadedListFile from 'Hooks/query-hooks/list/useDeleteUploadedListFile';
import useCancelListFileUpload from 'Hooks/query-hooks/list/useCancelListFileUpload';
import useIsMounted from 'Hooks/useIsMounted';
import appStyles from 'Routes/Routes.css';

import { FileUpload } from '@brighterion/br-ui';
import { FILE_UPLOAD_MAX_CHUNK_SIZE } from '@brighterion/utils/constants';
import { FILE_UPLOAD_STATUS, IMPORTER_START_UPLOAD_SCHEMA } from 'Common/enums';
import useGetImporterRequiredColumns from './hooks/useGetImporterRequiredColumns';
import ImporterActionButtons from './ImporterActionButtons';
import ImporterInstructions from './ImporterInstructions';
import { ImporterValidatedObjType, readFileForValidation } from './ImporterValidation';
import { formatBytes } from '../../sheetjs-helpers';

import styles from './Importer.css';

type ImporterProps = {
    listName: string;
    listId: number;
    listType: SingleListType;
    merchantSpecific: boolean;
};

/** Complete view of Import Entries from CSV View. Takes list details as Props */
const Importer = ({ listName, listType, listId, merchantSpecific }: ImporterProps) => {
    const isMounted = useIsMounted();
    const { t } = useTranslation();
    const requiredColumns = useGetImporterRequiredColumns(listType, merchantSpecific);
    const [state, setState] = useState<ImporterStateProps>({
        uploadProgress: 0,
        uploadDisabled: false,
        importDisabled: true,
        errorText: '',
        uploadId: '',
        fileUploadStatus: FILE_UPLOAD_STATUS.IDLE,
        partCompleted: 0,
        totalParts: 0,
        currentApiAbortController: new AbortController()
    });

    // separate state because file won't get set in objectified state
    const [currentFile, setCurrentFile] = useState<File | undefined>(undefined);

    const importerFileUploadMutation = useUploadListFile({ setState });

    const fileDeleteMutation = useDeleteUploadedListFile({});

    const cancelFileUploadMutation = useCancelListFileUpload({});

    // Upload file functions
    const uploadPart = async (partNum: number, uploadId: string) => {
        if (currentFile && uploadId) {
            const start = (partNum - 1) * FILE_UPLOAD_MAX_CHUNK_SIZE;
            const end = Math.min(partNum * FILE_UPLOAD_MAX_CHUNK_SIZE, currentFile.size);

            if (partNum <= state.totalParts && isMounted) {
                const chunk = currentFile.slice(start, end);
                const reader = new FileReader();
                reader.onload = async (event) => {
                    const partData = new Uint8Array(event.target?.result as ArrayBuffer);
                    const fileDetails = {
                        fileName: currentFile.name,
                        uploadId,
                        partNumber: partNum.toString(),
                        totalParts: state.totalParts.toString()
                    };

                    const formData = new FormData();
                    formData.append(
                        'fileDetails',
                        new Blob([JSON.stringify(fileDetails)], { type: 'application/json' })
                    );
                    formData.append('partData', new Blob([partData]));

                    const uploadProgress = Math.round((partNum / state.totalParts) * 100);
                    const newAbortController = new AbortController();
                    setState({
                        ...state,
                        uploadProgress,
                        currentApiAbortController: newAbortController
                    });
                    try {
                        await importerFileUploadMutation.mutateAsync({
                            formData,
                            abortController: newAbortController,
                            isInitiateUpload: false
                        });
                    } catch (error: any) {
                        // catches axios cancelled state here
                        if (!axios.isCancel(error)) {
                            setState({ ...state, errorText: t('File failed to upload due to a network error') });
                        }
                    }
                };
                reader.readAsArrayBuffer(chunk);
            }
        }
    };

    const initiateUpload = (selectedFile: File) => {
        const totalParts = Math.ceil(selectedFile.size / FILE_UPLOAD_MAX_CHUNK_SIZE);
        const newAbortController = new AbortController();
        setState({
            ...state,
            totalParts,
            currentApiAbortController: newAbortController,
            fileUploadStatus: FILE_UPLOAD_STATUS.INITIATED
        });
        setCurrentFile(selectedFile);

        const formData = new FormData();
        formData.append(IMPORTER_START_UPLOAD_SCHEMA.FILE_NAME, selectedFile.name);
        importerFileUploadMutation.mutate({ formData, abortController: newAbortController, isInitiateUpload: true });
    };

    // Following are Callback functions that are called by the br-ui FileUpload component.
    const handleFileDelete = () => {
        if (!currentFile) return;

        if (state.fileUploadStatus === FILE_UPLOAD_STATUS.UPLOADED) {
            fileDeleteMutation.mutate(currentFile.name);
        } else if (state?.uploadId && state.fileUploadStatus === FILE_UPLOAD_STATUS.UPLOADING) {
            const payload: IMPORTER_CANCEL_FILE_UPLOAD_SCHEMA = {
                fileName: currentFile.name,
                uploadId: state.uploadId
            };
            cancelFileUploadMutation.mutate(payload);
        }
        if (state.fileUploadStatus !== FILE_UPLOAD_STATUS.IDLE) {
            state.currentApiAbortController.abort();
        }
        setState({
            ...state,
            importDisabled: true,
            uploadProgress: 0,
            totalParts: 0,
            uploadId: '',
            partCompleted: 0,
            errorText: '',
            fileUploadStatus: FILE_UPLOAD_STATUS.IDLE
        });
        setCurrentFile(undefined);
    };

    const validationFunction = async (file: File) => {
        let errorMsg = '';

        const validatedObj: ImporterValidatedObjType = await readFileForValidation(file, requiredColumns);
        const { difference, isEmptyFile } = validatedObj;

        if (!isEmptyFile && difference.length) {
            errorMsg = `${t('ImporterValidation.missingColumns', {
                missingColumnNames: difference.map((v) => v?.join(' or ')).join(', ')
            })}`;
        } else if (isEmptyFile) {
            errorMsg = `${formatBytes(file.size)} - ${t('ImporterValidation.emptyFile')}`;
        }

        return { errorMsg };
    };

    const onFileChange = (files: File[]) => {
        const selectedFile = files[0];
        if (selectedFile && isMounted) {
            initiateUpload(selectedFile);
        }
    };

    useEffect(() => {
        const { uploadId, partCompleted, totalParts } = state;
        if (!currentFile || !uploadId || totalParts === 0) return;

        if (partCompleted === 0) {
            uploadPart(1, uploadId);
        } else if (partCompleted > 0 && partCompleted < totalParts) {
            uploadPart(partCompleted + 1, uploadId);
        } else if (partCompleted === totalParts) {
            setState((prevState) => ({
                ...prevState,
                fileUploadStatus: FILE_UPLOAD_STATUS.UPLOADED,
                importDisabled: false,
                uploadProgress: 0
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.uploadId, state.partCompleted]);

    return (
        <div className={appStyles.landingPage}>
            <ImporterActionButtons
                listName={listName}
                listId={listId}
                importDisabled={state.importDisabled || !isMounted}
                handleFileDelete={handleFileDelete}
                importFileName={currentFile?.name}
            />
            <div className={clsx(styles.FormContainer)}>
                <ImporterInstructions />
                <FileUpload
                    accept='.csv'
                    dataTestId='importExportFileUpload'
                    deleteLabel={state.fileUploadStatus !== FILE_UPLOAD_STATUS.UPLOADED ? t('Cancel') : t('Remove')}
                    disabled={state.uploadDisabled}
                    errorText={state.errorText}
                    maxSize={104857600}
                    maxLimit={1}
                    name='ListImport'
                    onChangeProp={onFileChange}
                    onDelete={handleFileDelete}
                    placeholderLink={t('ImporterPlaceholder.LinkText')}
                    placeholderMsg={t('ImporterPlaceholder.Message')}
                    progressPercent={state.uploadProgress}
                    validationFunction={validationFunction}
                />
            </div>
        </div>
    );
};

export default Importer;
